import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Star, Trash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReviewSection({ saleId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  const fetchReviews = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sales/${saleId}/reviews`);
      setReviews(data.reviews || []);
      if (user) {
        setHasReviewed(data.reviews?.some(r => r.user_id === user._id));
      }
    } catch {}
  }, [saleId, user]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please log in to leave a review'); return; }
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sales/${saleId}/reviews`, { rating, comment }, { withCredentials: true });
      toast.success('Review posted!');
      setRating(0); setComment(''); setHasReviewed(true);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post review');
    } finally { setSubmitting(false); }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      setHasReviewed(false);
      fetchReviews();
    } catch { toast.error('Failed to delete'); }
  };

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;

  return (
    <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="review-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Reviews</h2>
        {reviews.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="avg-rating">
            <Star size={18} weight="fill" className="text-[#FFD166]" />
            <span className="text-sm font-bold text-[#004E89]">{avgRating}</span>
            <span className="text-xs text-[#5C7A99]">({reviews.length})</span>
          </div>
        )}
      </div>

      {/* Review Form */}
      {user && !hasReviewed && (
        <form onSubmit={submitReview} className="mb-5 p-4 bg-[#F9F9F6] rounded-xl border-2 border-dashed border-[#004E89]" data-testid="review-form">
          <p className="text-xs font-bold text-[#004E89] uppercase tracking-wide mb-2">Leave a Review</p>
          <div className="flex gap-1 mb-3" data-testid="star-rating-input">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} type="button"
                onMouseEnter={() => setHoverRating(s)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(s)}
                className="transition-transform hover:scale-110"
                data-testid={`star-${s}`}
              >
                <Star size={28} weight={(hoverRating || rating) >= s ? 'fill' : 'regular'} className={(hoverRating || rating) >= s ? 'text-[#FFD166]' : 'text-[#C4C4C4]'} />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience (optional)"
            className="input-neo text-sm min-h-[60px] mb-3"
            rows={2}
            data-testid="review-comment-input"
          />
          <button type="submit" disabled={submitting || rating === 0} className="btn-primary py-2 px-5 text-sm" data-testid="submit-review-btn">
            {submitting ? 'Posting...' : 'Post Review'}
          </button>
        </form>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <p className="text-sm text-[#5C7A99] text-center py-4" data-testid="no-reviews">No reviews yet. Be the first!</p>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map(r => (
            <div key={r.id} className="p-3 bg-[#F9F9F6] rounded-xl border border-[#E5E7EB]" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#004E89]">{r.user_name}</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={14} weight={r.rating >= s ? 'fill' : 'regular'} className={r.rating >= s ? 'text-[#FFD166]' : 'text-[#C4C4C4]'} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#5C7A99]">{new Date(r.created_at).toLocaleDateString()}</span>
                  {user && (r.user_id === user._id || user.role === 'admin') && (
                    <button onClick={() => deleteReview(r.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-review-${r.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
              {r.comment && <p className="text-sm text-[#5C7A99]">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StarRating({ rating, count, size = 14 }) {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-1" data-testid="star-rating-display">
      <Star size={size} weight="fill" className="text-[#FFD166]" />
      <span className="text-xs font-bold text-[#004E89]">{rating}</span>
      {count !== undefined && <span className="text-[10px] text-[#5C7A99]">({count})</span>}
    </div>
  );
}
